import { Moment } from 'moment';
import { Row } from 'modules/billing/components/SimpleIndexPage/types';
import {
  KeyType,
  NAME,
  KEY_TYPE,
  JWT_REQUIRED,
  CUSTOM_EVENT_MANAGE_ALLOW_CREATION,
  CUSTOM_EVENT_MANAGE_ALLOW_CREATION_UNTIL,
} from './components/ApiKeyModal.constants';
import { API_KEY_KEY_TYPE_LABELS } from './constants';
import { ApiKey, API_KEY_TYPES, API_KEY_TYPE_CATEGORIES } from './types';

const { BULK_LOAD, READ_ONLY, SERVER_SIDE, STANDARD } = API_KEY_TYPES;

// JWT is not currently supported on Server-side or Readonly API keys
// Refactor this out when that is no longer the case
export const isJwtSupported = (keyType: API_KEY_TYPES): boolean =>
  ![BULK_LOAD, READ_ONLY, SERVER_SIDE].includes(keyType);

export const deriveKeyCategory = (key: ApiKey) =>
  [BULK_LOAD, READ_ONLY, SERVER_SIDE, STANDARD].includes(key.keyType)
    ? API_KEY_TYPE_CATEGORIES.SERVER_SIDE
    : API_KEY_TYPE_CATEGORIES.CLIENT_SIDE;

export const isClientSide = (keyCategory: string) => keyCategory === 'Client-side';

export const generateTypeOptions = (keyTypes: KeyType[]) =>
  keyTypes.map(({ label, value, type, description }) => ({
    id: value,
    label,
    value,
    categoryId: type,
    doubleLine: description,
  }));

/**
 * This function formats the `customEventManageAllowCreationUntil` field for the
 * create/update payload. It should only be sent/applied for Client-side aka.
 * JWT Supported API keys. This is because it has the effect of limiting the
 * working time of a given API key, which is necessary only for Client-side keys
 * which are more easily exposed to potential bad actors.
 */
export const convertAllowUntil = (values: Record<string, any>) =>
  isJwtSupported(values[KEY_TYPE] as API_KEY_TYPES) && values[CUSTOM_EVENT_MANAGE_ALLOW_CREATION]
    ? (values[CUSTOM_EVENT_MANAGE_ALLOW_CREATION_UNTIL] as Moment).toISOString()
    : null;

/**
 * This function formats the `jwtRequired` field for the create/update payload.
 * true should only be a valid value for JWT-supported aka. Client-side tokens.
 */
export const convertJwtRequired = (values: Record<string, any>) =>
  isJwtSupported(values[KEY_TYPE] as API_KEY_TYPES) ? values[JWT_REQUIRED] : false;

export const formulateCreateApiKeyPayload = (values: Record<string, any>) => ({
  keyType: values[KEY_TYPE],
  name: values[NAME],
  jwtRequired: convertJwtRequired(values),
  customEventManageAllowCreationUntil: convertAllowUntil(values),
});

export const formulateEditApiKeyPayload = (
  values: Record<string, any>,
  updatedValues: { enabled: boolean; keyHash: string },
) => {
  const { enabled, keyHash } = updatedValues;
  return { enabled, keyHash, ...formulateCreateApiKeyPayload(values) };
};

export const typeDescriptiveText = (key: ApiKey) => {
  const category = deriveKeyCategory(key);
  const jwt = key[JWT_REQUIRED] ? 'JWT required' : '';
  return jwt ? `${category} • ${jwt}` : category;
};

export const queryByFn = (query: string, row: Row) => {
  const q = query.toLowerCase();
  return (
    (row?.name as string).toLowerCase().includes(q) ||
    (row?.keyIdentifier as string).toLowerCase().includes(q)
  );
};
export const prependEllipses = (keyIdentifier: string) => `....${keyIdentifier}`;

export const keyTypeLabel = (keyType: API_KEY_TYPES) => API_KEY_KEY_TYPE_LABELS[keyType];
